import { apiClient, apiClientPaginated, ApiError } from '@/lib/api-client';
import type {
  ApproveLateArrivalInput,
  AttendanceListParams,
  AttendanceListResult,
  AttendanceRecord,
  AttendanceStats,
  CreateShiftInput,
  Shift,
  ShiftListParams,
  ShiftListResult,
  ShiftStats,
  ShiftStatsParams,
  ScheduleStaffInput,
  UpdateAttendanceInput,
  UpdateShiftInput,
  CreateSwapInput,
  SwapListParams,
  SwapListResult,
  ShiftSwapRequest,
} from './types';

async function withFallback<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ApiError && (err.statusCode === 404 || err.statusCode === 501)) {
      return fallback;
    }
    throw err;
  }
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return `?${entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&')}`;
}

const EMPTY_SHIFT_LIST: ShiftListResult = { data: [], meta: { page: 1, limit: 20, total: 0, totalPages: 0 } };
const EMPTY_SHIFT_STATS: ShiftStats = { total: 0, active: 0, locations: 0 };
const EMPTY_ATTENDANCE_LIST: AttendanceListResult = { data: [], meta: { page: 1, limit: 20, total: 0, totalPages: 0 } };
const EMPTY_ATTENDANCE_STATS: AttendanceStats = { total: 0, present: 0, absent: 0, late: 0 };

export const shiftsApi = {
  shifts: (params: ShiftListParams) =>
    withFallback(
      () => apiClientPaginated<ShiftListResult['data']>(
        `/shifts${buildQuery({ page: params.page, location_id: params.location_id, is_active: params.is_active, search: params.search })}`,
      ) as Promise<ShiftListResult>,
      EMPTY_SHIFT_LIST,
    ),

  // KPI totals computed over the whole shift dataset. Deliberately omits the
  // is_active status filter and pagination so "Active Shifts" / "Locations
  // Covered" reflect business reality, not just the current page (issue #170).
  shiftStats: (params: ShiftStatsParams) =>
    withFallback(
      () => apiClient<ShiftStats>(
        `/shifts/stats${buildQuery({ location_id: params.location_id, search: params.search })}`,
      ),
      EMPTY_SHIFT_STATS,
    ),

  shift: (id: string) =>
    withFallback(() => apiClient<Shift>(`/shifts/${id}`), null),

  createShift: (input: CreateShiftInput) =>
    apiClient<Shift>('/shifts', { method: 'POST', body: input }),

  updateShift: (id: string, input: UpdateShiftInput) =>
    apiClient<Shift>(`/shifts/${id}`, { method: 'PATCH', body: input }),

  deactivateShift: (id: string) =>
    apiClient<Shift>(`/shifts/${id}`, { method: 'DELETE' }),

  attendance: (params: AttendanceListParams) =>
    withFallback(
      () => apiClientPaginated<AttendanceListResult['data']>(
        `/attendance${buildQuery({
          page: params.page, location_id: params.location_id, user_id: params.user_id,
          shift_id: params.shift_id, start_date: params.start_date, end_date: params.end_date,
          status: params.status,
        })}`,
      ) as Promise<AttendanceListResult>,
      EMPTY_ATTENDANCE_LIST,
    ),

  // KPI totals (Present / Absent / Late) computed over the whole filtered
  // attendance range, independent of pagination — so the cards reflect every
  // record in the selected range, not just the current page (issue #173).
  attendanceStats: (params: AttendanceListParams) =>
    withFallback(
      () => apiClient<AttendanceStats>(
        `/attendance/stats${buildQuery({
          location_id: params.location_id, user_id: params.user_id,
          shift_id: params.shift_id, start_date: params.start_date,
          end_date: params.end_date, status: params.status,
        })}`,
      ),
      EMPTY_ATTENDANCE_STATS,
    ),

  // Schedule (assign) a staff member to a shift on a given date. Backend:
  // POST /attendance ("Schedule a staff member for a shift", MANAGER+).
  scheduleStaff: (input: ScheduleStaffInput) =>
    apiClient<AttendanceRecord>('/attendance', { method: 'POST', body: input }),

  updateAttendance: (id: string, input: UpdateAttendanceInput) =>
    apiClient<AttendanceRecord>(`/attendance/${id}`, { method: 'PATCH', body: input }),

  // Approve / reject a LATE arrival — preserves the LATE status and records who
  // decided and when, instead of overwriting the status (issue #174).
  approveLateArrival: (id: string, input: ApproveLateArrivalInput) =>
    apiClient<AttendanceRecord>(`/attendance/${id}/late-approval`, { method: 'PATCH', body: input }),

  // ── Shift swaps ──────────────────────────────────────────────────────────
  createSwap: (input: CreateSwapInput) =>
    apiClient<ShiftSwapRequest>('/shift-swaps', { method: 'POST', body: input }),
  listSwaps: (params: SwapListParams) =>
    apiClientPaginated<SwapListResult['data']>(
      `/shift-swaps${buildQuery({ status: params.status, user_id: params.user_id, shift_date: params.shift_date, page: params.page })}`,
    ) as Promise<SwapListResult>,
  approveSwap: (id: string) =>
    apiClient<ShiftSwapRequest>(`/shift-swaps/${id}/approve`, { method: 'PATCH' }),
  rejectSwap: (id: string) =>
    apiClient<ShiftSwapRequest>(`/shift-swaps/${id}/reject`, { method: 'PATCH' }),
  cancelSwap: (id: string) =>
    apiClient<ShiftSwapRequest>(`/shift-swaps/${id}/cancel`, { method: 'PATCH' }),
};
